import {
  ChangeDetectionStrategy,
  Component,
  signal,
  input
} from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { RouterModule } from "@angular/router";
import {
  AlertController,
  IonicModule,
  LoadingController,
  ModalController,
  ToastController,
  isPlatform,
} from "@ionic/angular";
import { Store } from "@ngrx/store";
import { ChangeDetectorRef } from "@angular/core";
import {
  addLesson,
  updateLesson,
} from "src/app/calendar/ngrx/calendar.actions";
import { ModeService } from "../../../utils/mode.service";
import { Tab1Page } from "src/app/tab1/tab1.page";
import { CounterInputComponent } from "src/app/shared/components/counter-input/counter-input.component";
import { convertAppointment } from "src/app/utils/ConvertAppointments";
import * as customerSelector from "src/app/auth/ngrx/customer.selectors";
import { distinctUntilChanged, first, skip } from "rxjs";
import { loadServices } from "src/app/settings/ngrx/settings.action";
import * as settingsSelector from 'src/app/settings/ngrx/settings.selectors';
import { AuthService } from "src/app/supabase/services/auth.service";
import { selectIsLoading } from "src/app/calendar/ngrx/calendar.selectors";
import { computed } from '@angular/core';
import { DialogService } from "src/app/shared/services/dialog.service";
import { Plans } from "src/app/shared/models/plans.enum";

@Component({
    selector: "app-new-lesson-dialog",
    templateUrl: "./new-lesson-dialog.component.html",
    styleUrls: ["./new-lesson-dialog.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        IonicModule,
        FormsModule,
        RouterModule,
        ReactiveFormsModule,
        CounterInputComponent,
    ]
})
export class NewLessonDialogComponent {
  readonly lesson = input<any>(null);

  lessonForm: FormGroup;
  completeStartDate: Date;
  localCompleteStartDate: string;

  lfaEndDate: Date = null;

  completeEndDate: Date;
  localCompleteEndDate: string;

  customer$ = this.store.select(customerSelector.selectCustomer);

  customerSignal = this.store.selectSignal(customerSelector.selectCustomer);
  servicesSignal = this.store.selectSignal(settingsSelector.selectServices);
  customerEntitlementsSignal = this.store.selectSignal(customerSelector.selectEntitlements);
  offset: any;
  lessonDuration: number = 45;
  lessonAmount: number = 2;
  lessonPrize: number = 80;
  serviceId: number;
  subscriptionPayment: boolean;
  sendReminder: boolean;
  sms_reminder: boolean;
  trackUpdates = signal(0);
  private isDialogOpen: boolean = false;

  filteredServices = computed(() => {
    this.trackUpdates();
    const selectedStudent = this.lessonForm.get('student_id')?.value;
    if (selectedStudent && selectedStudent.category) {
      const filterServices = this.servicesSignal().filter(service => service.category === selectedStudent.category);
      if (this.servicesSignal().length > 0 && filterServices.length === 0 && !this.isDialogOpen) {
        this.isDialogOpen = true;
        this.dialogService.openSettings('lessons', 'Wir haben leider keine passenden Leistungen für die Kategorie von ' + selectedStudent.forename + ' gefunden. Möchtest du eine neue Lektion für die Kategorie anlegen?')
          .then(result => {
            this.isDialogOpen = false;
            if (result) {
              this.cancel();
            }
          });
      }
      if (!filterServices.some(service => service.service_id === this.serviceId)) {
        this.serviceId =  filterServices[0]?.service_id;
        this.setSubscriptionValues();
        this.patchFormValues();
      }
      return filterServices;
    }
    return this.servicesSignal();
  });

  constructor(
    public modeService: ModeService,
    private store: Store<any>,
    private fb: FormBuilder,
    private authService: AuthService,
    public modalController: ModalController,
    private cdr: ChangeDetectorRef,
    private loadingCtrl: LoadingController,
    private alertController: AlertController,
    private toastCtrl: ToastController,
    private dialogService: DialogService
  ) {
    // for the completeStartDate we want to have the current time but only have half hour intervals
    this.completeStartDate = new Date(
      Math.ceil(new Date().getTime() / (30 * 60 * 1000)) * (30 * 60 * 1000)
    );
    this.completeEndDate = new Date(
      this.completeStartDate.getTime() +
        (this.lessonDuration ?? 45) * 60000
    ); // Add 45 minutes

    this.offset = new Date().getTimezoneOffset(); // Get the timezone offset in minutes
    this.localCompleteStartDate = this.dateToInputString(
      this.completeStartDate,
      this.offset
    );
    this.localCompleteEndDate = this.dateToInputString(
      this.completeEndDate,
      this.offset
    );

    this.lessonForm = this.fb.group({
      student_id: ["", Validators.required],
      location: [""],
      lessons: [1, Validators.required],
      start_time: [this.localCompleteStartDate, Validators.required],
      end_time: [this.localCompleteEndDate, Validators.required],
      prize: ["", Validators.required],
      type: ["Fahrstunde", Validators.required],
      service_id: [undefined, Validators.required],
      notes: [""],
      sendReminder: [false],
      sms_reminder: [false],
      user_id: [""],
      local_calendar_id: [""],
      subscription_payment: [true],
    });
  }

  ngOnInit() {
    this.customer$.pipe(first()).subscribe((customer) => {
      this.lessonAmount = customer.lesson_amount;
      this.serviceId = this.lesson()?.ServiceId ?? customer.default_service_id ?? this.servicesSignal()[0]?.service_id;
      this.setSubscriptionValues();
      
      this.sendReminder = customer.send_appointment_confirmation;
      this.sms_reminder = (this.customerEntitlementsSignal()?.includes(Plans.premium) || this.customerEntitlementsSignal()?.includes(Plans.premium_promo)) ? customer.send_appointment_reminder : false;
      this.patchFormValues();
    });

    const lesson = this.lesson();
    if (lesson && lesson.Id) {
      var lessonToUpdate: any = convertAppointment(lesson);
      lessonToUpdate.student_id = lessonToUpdate.student;
      this.lfaEndDate = lessonToUpdate.student.lfa_end;
      lessonToUpdate.start_time = this.dateToInputString(
        lessonToUpdate.start_time,
        0
      );
      lessonToUpdate.end_time = this.dateToInputString(
        lessonToUpdate.end_time,
        0
      );

      this.lessonForm.patchValue(lessonToUpdate);
    } else if (lesson?.student) {
      this.lfaEndDate = lesson.student.lfa_end;
      this.lessonForm.patchValue({
        student_id: lesson.student,
        location: lesson.location
      });
    } else if (lesson?.start_time) {
      const startTime = new Date(lesson.start_time);
      const offsetMinutes = startTime.getTimezoneOffset();
      const endTime = new Date(startTime);
      endTime.setMinutes(
        startTime.getMinutes() +
          (this.lessonDuration ?? 45) * this.lessonForm.value.lessons
      );
      const formattedEndTime = this.dateToInputString(endTime, offsetMinutes);
      this.lessonForm.patchValue({
        start_time: this.dateToInputString(lesson.start_time, this.offset),
        end_time: formattedEndTime
      });
    }

    if (!(this.customerEntitlementsSignal()?.includes(Plans.premium) || this.customerEntitlementsSignal()?.includes(Plans.premium_promo))) {
      this.lessonForm.get('sms_reminder').disable()
    }

    this.lessonForm.get('student_id')?.valueChanges.subscribe((student) => {
      this.trackUpdates.set(this.trackUpdates() + 1);
    });
  }

  showPremiumFeatureMessage() {
    this.toastCtrl.create({
      message: 'Die SMS-Erinnerungen können nur mit einem Premium-Abonnement genutzt werden.',
      duration: 3000,
    }).then(toast => toast.present());
  }

  ionViewDidEnter() {
    this.store.dispatch(loadServices());
  }

  patchFormValues() {
    this.lessonForm.patchValue({
      lessons: this.lessonAmount,
      service_id: this.serviceId,
      sendReminder: this.sendReminder,
      sms_reminder: this.sms_reminder,
      prize: this.lessonPrize * this.lessonForm.value.lessons,
      subscription_payment: this.subscriptionPayment
    });
    this.updateFormValues();
  }

  private dateToInputString(date: Date, offsetMinutes: number): string {
    date.setMinutes(date.getMinutes() - offsetMinutes); // Adjust for timezone offset
    return date.toISOString().slice(0, 16);
  }

  cancel() {
    this.modalController.dismiss();
  }

  async save() {
    if (this.lessonForm.valid) {
      const lesson = { ...this.lessonForm.value};

      if (this.lfaEndDate !== null) {
        if (this.lfaEndDate < lesson.start_time) {
          const saveAlertButtons = [
            {
              text: "Abbrechen",
              role: "cancel",
              handler: () => {
                this.alertController.dismiss();
                return;
              },
            },
            {
              text: "OK",
              role: "confirm",
              handler: () => {
                this.continueSave();
                this.alertController.dismiss();
              },
            },
          ];

          const alert = await this.alertController.create({
            header: "Achtung!",
            message: "Der Lernfahrausweis von " + lesson.student_id.forename + " ist an diesem Datum nicht mehr gültig. Möchtest du trotzdem fortfahren?",
            buttons: saveAlertButtons,
          });
      
          await alert.present();
        } else {
          this.continueSave();
        }
      } else {
        this.continueSave();
      }
    }
  }

  async continueSave() {      
    const lesson = { ...this.lessonForm.value};
    const googleCalendarId = this.customerSignal().google_calendar_id;
    const localCalendarConnected = this.customerSignal().local_calendar_connected;

    lesson.user_id = await this.authService.getCurrentUserId();
    lesson.subject = lesson.type + " " + lesson.student_id.forename;
    lesson.student = lesson.student_id;
    lesson.student_id = lesson.student_id.student_id;
    lesson.sendReminder = lesson.student.mail_student ? lesson.sendReminder : false;
    lesson.sms_reminder = lesson.student.mobile_no ? lesson.sms_reminder : false;
    let loading;

    const loading$ = this.store.select(selectIsLoading);
    const subscription = loading$.pipe(distinctUntilChanged(), skip(1)).subscribe((isLoading) => {
      if (!isLoading) {
        loading.dismiss();
        this.modalController.dismiss();
        subscription.unsubscribe();
      }
    });

    const lessonValue = this.lesson();
    if (lessonValue && Object.keys(lessonValue).length !== 0 && lessonValue.Id) {
      loading = await this.loadingCtrl.create({
        message: "Termin wird aktualisiert...",
      });
  
      await loading.present();
      lesson.appointment_id = lessonValue.Id;
      lesson.google_id = lessonValue.GoogleId;
      lesson.local_calendar_id = lessonValue.LocalCalendarId;
      this.store.dispatch(updateLesson({ lesson, googleCalendarId }));
    } else {
      loading = await this.loadingCtrl.create({
        message: "Termin wird erstellt...",
      });
  
      await loading.present();
      this.store.dispatch(addLesson({ lesson, googleCalendarId, localCalendarConnected }));
    }
  }

  updateFormValues() {
    const startTimeString = this.lessonForm.value.start_time;
    const startTime = new Date(startTimeString);
    const offsetMinutes = startTime.getTimezoneOffset(); // Get the timezone offset in minutes
    startTime.setMinutes(
      startTime.getMinutes() +
        (this.lessonDuration ?? 45) * this.lessonForm.value.lessons
    );
    const formattedEndTime = this.dateToInputString(startTime, offsetMinutes);

    const prize = this.lessonPrize ?? 0;
    const lessons = this.lessonForm.value.lessons;
    const totalPrize = prize * lessons;

    this.lessonForm.patchValue({ end_time: formattedEndTime });
    this.lessonForm.patchValue({ prize: totalPrize });
  }

  updateToService() {
    const selectedServiceId = this.lessonForm.value.service_id;
    const selectedService = this.servicesSignal().find(
      (service) => service?.service_id === selectedServiceId
    );

    if (selectedService) {
      this.lessonPrize = selectedService.prize;
      this.lessonDuration = selectedService.duration;
      this.lessonForm.patchValue({
        prize: selectedService.prize * this.lessonForm.value.lessons,
        subscription_payment: selectedService.subscription,
      });
      this.updateFormValues();
    }
  }

  private setSubscriptionValues() {
    const subscription = this.servicesSignal().find(
      (service) => service?.service_id === this.serviceId
    );
    this.subscriptionPayment = subscription?.subscription;
    this.lessonDuration = subscription?.duration;
    this.lessonPrize = subscription?.prize;
  }

  async selectStudent() {
    const modal = await this.modalController.create(
      isPlatform("desktop") ||
        isPlatform("phablet") ||
        isPlatform("ipad") ||
        (isPlatform("tablet") && !isPlatform("mobile") && !isPlatform("iphone"))
        ? {
            component: Tab1Page,
            componentProps: {
              isSelection: signal(true),
            },
            canDismiss: true,
            showBackdrop: true,
          }
        : {
            component: Tab1Page,
            componentProps: {
              isSelection: signal(true),
            },
            canDismiss: true,
            showBackdrop: true,
            backdropDismiss: true,
            breakpoints: [0, 0.6, 1],
            initialBreakpoint: 0.6,
          }
    );
    modal.onDidDismiss().then((result) => {
      if (result.data) {
        this.lessonForm.controls["student_id"].setValue(result.data);
        this.lfaEndDate = result.data.lfa_end;
        this.lessonForm.controls["location"].setValue(result.data.location);
        this.cdr.detectChanges();
      }
    });

    return modal.present();
  }
}
